import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { usePaginatedProducts } from '../hooks/usePaginatedProducts';
import { 
  Save, 
  Eye, 
  EyeOff, 
  Bot, 
  Settings,
  TestTube,
  UserCheck,
  Wrench,
  User,
  Plus,
  Edit2,
  Trash2,
  Database
} from 'lucide-react';
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialLight } from "react-syntax-highlighter/dist/esm/styles/prism";

const DEMO_KEY = 'demo-key-123';

const Configurations = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('ai');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Account and Vector Store State
  const [accountInfo, setAccountInfo] = useState({ account_id: null });
  const [vectorStores, setVectorStores] = useState([]);
  const [defaultVectorStoreIndex, setDefaultVectorStoreIndex] = useState(null);
  const [showAddVectorStore, setShowAddVectorStore] = useState(false);
  const [editingVectorStore, setEditingVectorStore] = useState(null);
  const [vectorStoreForm, setVectorStoreForm] = useState({
    name: '',
    description: '',
    assistant: {
      name: '',
      instructions: '',
      model: ''
    }
  });
  const [creatingVectorStore, setCreatingVectorStore] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Separate configurations for different AI providers
  const [openaiConfig, setOpenaiConfig] = useState({
    provider: 'openai',
    openai_api_key: '',
    model: 'gpt-3.5-turbo',
    temperature: 0.7,
    max_tokens: 1000
  });
  
  const [anthropicConfig, setAnthropicConfig] = useState({
    provider: 'anthropic',
    api_key: '',
    model: 'claude-3-sonnet-20240229',
    temperature: 0.7,
    max_tokens: 1000
  });

  // Pinecone config
  const [pineconeConfig, setPineconeConfig] = useState({ api_key: '', index: '', environment: '' });

  // State for available models
  const [availableModels, setAvailableModels] = useState({
    openai: [],
    claude: []
  });
  const [loadingModels, setLoadingModels] = useState(false);
  
  const [preferredAIEngine, setPreferredAIEngine] = useState({
    preferred_engine: 'auto',
    description: 'System will automatically choose the best available AI engine'
  });
  const [openaiKeyConfigured, setOpenaiKeyConfigured] = useState(false);

  // Guards to avoid overwriting OpenAI key after initial load or user interaction
  const openaiKeyInitialized = useRef(false);
  const openaiKeyTouched = useRef(false);
  
  const [databaseConfig, setDatabaseConfig] = useState({
    host: 'localhost',
    port: '5432',
    name: 'ai_tech_assistant',
    user: 'postgres',
    password: '',
    ssl_mode: 'prefer'
  });
  
  const [generalConfig, setGeneralConfig] = useState({
    app_name: 'AI Tech Assistant',
    chunk_size: 1000,
    chunk_overlap: 200,
    max_file_size: 10,
    rate_limit_per_minute: 60
  });

  // AI Agents Configuration
  const [aiSalesAgent, setAiSalesAgent] = useState({
    enabled: false,
    chime_in_time: 30, // seconds
    product_scope: 'all', // 'all' or 'selected'
    selected_products: [],
    caching_enabled: true,
    system_prompt: 'You are a helpful AI sales agent that assists customers with product information. Analyze the user\'s needs and provide relevant product recommendations based on available documentation and specifications. Always be friendly, informative, and focus on helping the customer find the right solution.'
  });

  const [aiTechAssistant, setAiTechAssistant] = useState({
    enabled: false,
    description: 'Answers any product related and technical support type questions based on the manuals and spec sheets of the product.',
    system_prompt: 'You are a technical support specialist AI assistant. Provide accurate, detailed technical information and troubleshooting guidance based on product manuals, specification sheets, and documentation. Focus on solving technical problems and answering specific technical questions with precision and clarity.'
  });

  // Paginated products hook
  const {
    selectedProductsData,
    availableProductsData,
    selectedProducts,
    selectedProductsPagination,
    availableProductsPagination,
    searchTerm,
    setSearchTerm,
    loadingSelected,
    loadingAvailable,
    initializeSelectedProducts,
    addProduct,
    removeProduct,
    clearAllProducts,
    selectAllFromCurrentPage,
    loadSelectedProductsPage,
    loadAvailableProductsPage
  } = usePaginatedProducts();
  
  // AI Agents vertical tab state
  const [activeAgentTab, setActiveAgentTab] = useState('sales-agent');

  // AI Sales Agent testing state
  const [testingSalesAgent, setTestingSalesAgent] = useState(false);
  const [testProductUrl, setTestProductUrl] = useState('https://www.herculesindustries.com/item/allied-commercial-zhb060s4bn-y');
  const [testSelectedProduct, setTestSelectedProduct] = useState('');
  const [testResults, setTestResults] = useState('');
  const [showTestResults, setShowTestResults] = useState(false);

  // useEffect calls will be placed after function declarations to avoid temporal dead zone

  // Function to get token limits for different AI models
  const getModelTokenLimit = (modelName) => {
    if (!modelName) return null;
    
    const modelLower = modelName.toLowerCase();
    
    // OpenAI models
    if (modelLower.includes('gpt-5')) {
      return '8K tokens';
    } else if (modelLower.includes('gpt-4')) {
      return '128K tokens';
    } else if (modelLower.includes('gpt-3.5')) {
      return '16K tokens';
    }
    
    // Anthropic models
    if (modelLower.includes('claude-4')) {
      return '200K tokens';
    } else if (modelLower.includes('claude-3.5')) {
      return '200K tokens';
    } else if (modelLower.includes('claude-3')) {
      return '200K tokens';
    } else if (modelLower.includes('claude-2')) {
      return '100K tokens';
    }
    
    return null;
  };

  const formatModelDisplayName = (model) => {
    let displayName = model.id;
    
    // Add token limit if available
    const tokenLimit = getModelTokenLimit(model.id);
    if (tokenLimit) {
      displayName += ` (${tokenLimit})`;
    }
    
    // Add description if available
    if (model.description) {
      displayName += ` - ${model.description}`;
    }
    
    // Add creation date if available
    if (model.created) {
      const date = new Date(model.created * 1000);
      displayName += ` (${date.toLocaleDateString()})`;
    }
    
    return displayName;
  };

  const fetchAvailableModels = useCallback(async () => {
    try {
      setLoadingModels(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      // Fetch available models from both AI engines
      const [openaiResponse, claudeResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/v1/config/ai/models/openai`, { headers }),
        fetch(`${API_BASE_URL}/api/v1/config/ai/models/claude`, { headers })
      ]);

      if (openaiResponse.ok) {
        const openaiData = await openaiResponse.json();
        if (openaiData.available && openaiData.models) {
          setAvailableModels(prev => ({ ...prev, openai: openaiData.models }));
        }
      }

      if (claudeResponse.ok) {
        const claudeData = await claudeResponse.json();
        if (claudeData.available && claudeData.models) {
          setAvailableModels(prev => ({ ...prev, claude: claudeData.models }));
        }
      }
    } catch (error) {
      console.error('Error fetching available models:', error);
    } finally {
      setLoadingModels(false);
    }
  }, [getAuthHeaders]);



  const fetchConfigurations = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers || Object.keys(headers).length === 0) {
        console.warn('No auth headers available for fetching configurations');
        return;
      }
      console.log('Fetching configurations with headers:', headers);

      // Fetch AI configurations
      const aiResponse = await fetch(`${API_BASE_URL}/api/v1/config/ai`, { headers });
      if (aiResponse.ok) {
        const aiData = await aiResponse.json();
        console.log('AI Configuration data received:', aiData);
        if (aiData.openai) {
          const cfgRaw = aiData.openai.config || {};
          const serverKey = cfgRaw.api_key || cfgRaw.API_KEY || cfgRaw.key || '';
          const masked = serverKey ? `${serverKey.substring(0,4)}...${serverKey.substring(Math.max(0, serverKey.length-4))}` : '';
          console.log('OpenAI key from DB (raw):', serverKey);
          console.log('OpenAI key from DB (masked):', masked);
          if (serverKey && serverKey !== DEMO_KEY) {
            setOpenaiKeyConfigured(true);
          }
          setOpenaiConfig(prev => ({
            ...prev,
            provider: 'openai',
            model: cfgRaw.model || prev.model || 'gpt-3.5-turbo',
            temperature: typeof cfgRaw.temperature === 'number' ? cfgRaw.temperature : (prev.temperature ?? 0.7),
            max_tokens: typeof cfgRaw.max_tokens === 'number' ? cfgRaw.max_tokens : (prev.max_tokens ?? 1000),
          }));
          console.log('OpenAI config updated:', aiData.openai);
        }
        if (aiData.anthropic) {
          const cfgA = aiData.anthropic.config || {};
          setAnthropicConfig(prev => ({
            ...prev,
            provider: 'anthropic',
            api_key: cfgA.api_key || prev.api_key || '',
            model: cfgA.model || prev.model || 'claude-3-sonnet-20240229',
            temperature: typeof cfgA.temperature === 'number' ? cfgA.temperature : (prev.temperature ?? 0.7),
            max_tokens: typeof cfgA.max_tokens === 'number' ? cfgA.max_tokens : (prev.max_tokens ?? 1000),
          }));
          console.log('Anthropic config updated:', aiData.anthropic);
        }
      } else {
        console.error('Failed to fetch AI configurations:', aiResponse.status, await aiResponse.text());
      }

      // Fetch database configuration
      const dbResponse = await fetch(`${API_BASE_URL}/api/v1/config/database`, { headers });
      if (dbResponse.ok) {
        const data = await dbResponse.json();
        console.log('Database configuration data received:', data);
        if (data.config) {
          setDatabaseConfig(prev => ({ ...prev, ...data.config }));
          console.log('Database config updated:', data.config);
        }
      }

      // Fetch preferred AI engine configuration
      const preferredResponse = await fetch(`${API_BASE_URL}/api/v1/config/ai/preferred`, { headers });
      if (preferredResponse.ok) {
        const data = await preferredResponse.json();
        console.log('Preferred AI configuration data received:', data);
        // Extract only the configuration fields, exclude updated_at
        const { updated_at, ...config } = data;
        setPreferredAIEngine(prev => ({ ...prev, ...config }));
        console.log('Preferred AI config updated:', config);
      }

      // Fetch general configuration
      const generalResponse = await fetch(`${API_BASE_URL}/api/v1/config/general`, { headers });
      if (generalResponse.ok) {
        const data = await generalResponse.json();
        console.log('General configuration data received:', data);
        if (data.config) {
          setGeneralConfig(prev => ({ ...prev, ...data.config }));
          console.log('General config updated:', data.config);
        }
      }

      // Fetch AI agents configuration
      const agentsResponse = await fetch(`${API_BASE_URL}/api/v1/config/agents`, { headers });
      if (agentsResponse.ok) {
        const data = await agentsResponse.json();
        console.log('AI Agents configuration data received:', data);
        if (data.sales_agent) {
          setAiSalesAgent(prev => ({ ...prev, ...data.sales_agent }));
          // Initialize selected products from the configuration
          if (data.sales_agent.selected_products) {
            initializeSelectedProducts(data.sales_agent.selected_products);
          }
        }
        if (data.tech_assistant) {
          setAiTechAssistant(prev => ({ ...prev, ...data.tech_assistant }));
        }
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
    }
  }, [getAuthHeaders, initializeSelectedProducts]);

  // useEffect hooks - placed after function declarations to avoid temporal dead zone
  useEffect(() => {
    fetchConfigurations();
    fetchAvailableModels();
    // Load pinecone config
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/config/pinecone`, { headers: getAuthHeaders() });
        if (response.ok) {
          const data = await response.json();
          setPineconeConfig({
            api_key: data.config?.api_key || '',
            index: data.config?.index || '',
            environment: data.config?.environment || data.config?.host || ''
          });
        }
      } catch (e) {
        console.error('Failed to load Pinecone config', e);
      }
    })();
  }, [fetchConfigurations, fetchAvailableModels]);

  // Initialize test product selection when sales agent config changes
  useEffect(() => {
    if (aiSalesAgent.selected_products && aiSalesAgent.selected_products.length > 0 && !testSelectedProduct) {
      setTestSelectedProduct(aiSalesAgent.selected_products[0]);
    }
  }, [aiSalesAgent.selected_products, testSelectedProduct]);

  // Note: Database configuration is currently hidden - function commented out to avoid ESLint warnings
  // const handleDatabaseSave = async () => {
  //   try {
  //     setSaving(true);
  //     const response = await fetch(`${API_BASE_URL}/api/v1/config/database`, {
  //       method: 'PUT',
  //       headers: {
  //         ...getAuthHeaders(),
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify(databaseConfig)
  //     });

  //     if (response.ok) {
  //       alert('Database configuration saved successfully!');
  //       // Refresh configurations after successful save
  //       fetchConfigurations();
  //     } else {
  //       const errorData = await response.text();
  //       console.error('Save failed:', response.status, errorData);
  //       alert(`Failed to save Database configuration: ${response.status} - ${errorData}`);
  //     }
  //   } catch (error) {
  //     console.error('Error saving database configuration:', error);
  //     alert('Failed to save database configuration');
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  const handleOpenAISave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/ai`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'openai',
          api_key: openaiConfig.openai_api_key,
          model: openaiConfig.model,
          temperature: openaiConfig.temperature,
          max_tokens: openaiConfig.max_tokens
        })
      });

      if (response.ok) {
        alert('OpenAI configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save OpenAI configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving OpenAI configuration:', error);
      alert('Failed to save OpenAI configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAnthropicSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/ai`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({...anthropicConfig, type: 'anthropic'})
      });

      if (response.ok) {
        alert('Anthropic configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save Anthropic configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving Anthropic configuration:', error);
      alert('Failed to save Anthropic configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneralSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/general`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(generalConfig)
      });

      if (response.ok) {
        alert('General configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save General configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving general configuration:', error);
      alert('Failed to save general configuration');
    } finally {
      setSaving(false);
    }
  };

  // Note: Database configuration is currently hidden - function commented out to avoid ESLint warnings
  // const testDatabaseConnection = async () => {
  //   try {
  //     const response = await fetch(`${API_BASE_URL}/api/v1/health/detailed`, {
  //       headers: getAuthHeaders()
  //     });
  //     if (response.ok) {
  //       const data = await response.json();
  //       if (data.services?.database?.status === 'healthy') {
  //         alert('Database connection successful!');
  //       } else {
  //         alert('Database connection failed. Check your configuration.');
  //       }
  //     }
  //   } catch (error) {
  //     alert('Failed to test database connection');
  //   }
  // };

  const testAIConnection = async (provider) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        alert('Authentication required. Please log in again.');
        return;
      }

      // Get the current configuration for the provider
      let config;
      let model;
      if (provider === 'OpenAI') {
        config = openaiConfig;
        model = openaiConfig.model;
      } else if (provider === 'Anthropic') {
        config = anthropicConfig;
        model = anthropicConfig.model;
      } else {
        alert(`Unknown provider: ${provider}`);
        return;
      }

      // Check if API key is provided
      if (!config.api_key || config.api_key.trim() === '' || config.api_key === DEMO_KEY) {
        alert(`${provider} API key is required. Please enter a valid API key first.`);
        return;
      }

      // Check if model is selected
      if (!model || model.trim() === '') {
        alert(`${provider} model is required. Please select a valid model first.`);
        return;
      }

      // Show testing message
      const keyPreview = (openaiConfig.openai_api_key || '').substring(0, 8);
      alert(`Testing ${provider} connection...\n\nAPI Key: ${keyPreview}...\nModel: ${model}\n\nThis will validate both the API key and model name.`);

      // Make a real API call to test the configuration
      const response = await fetch(`${API_BASE_URL}/api/v1/config/ai/test`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          engine_type: provider.toLowerCase(),
          api_key: openaiConfig.openai_api_key,
          model_name: model
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert(`✅ ${provider} connection test successful!\n\nAPI Key: Valid ✅\nModel: ${model} ✅\nResponse: ${data.message}\n\nYour ${provider} configuration is working correctly.`);
        } else {
          alert(`❌ ${provider} connection test failed!\n\nAPI Key: ${data.api_key_valid ? 'Valid ✅' : 'Invalid ❌'}\nModel: ${data.model_valid ? 'Valid ✅' : 'Invalid ❌'}\n\nError: ${data.message}\n\nPlease check your configuration and try again.`);
        }
      } else {
        const errorData = await response.json();
        alert(`❌ ${provider} connection test failed!\n\nServer Error: ${response.status}\nMessage: ${errorData.detail || 'Unknown error'}\n\nPlease check your configuration and try again.`);
      }
    } catch (error) {
      console.error(`Error testing ${provider} connection:`, error);
      alert(`❌ ${provider} connection test failed!\n\nError: ${error.message}\n\nPlease check your configuration and try again.`);
    }
  };

  const handlePreferredAISave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/config/ai/preferred`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferredAIEngine)
      });

      if (response.ok) {
        alert('Preferred AI engine configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save Preferred AI engine configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving preferred AI engine configuration:', error);
      alert('Error saving preferred AI engine configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSalesAgentSave = async () => {
    try {
      setSaving(true);
      // Get current tech assistant config to preserve it
      const currentConfigResponse = await fetch(`${API_BASE_URL}/api/v1/config/agents`, {
        headers: getAuthHeaders()
      });
      
      let currentTechAssistant = aiTechAssistant;
      if (currentConfigResponse.ok) {
        const currentConfig = await currentConfigResponse.json();
        currentTechAssistant = currentConfig.tech_assistant || aiTechAssistant;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/config/agents`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sales_agent: {
            ...aiSalesAgent,
            selected_products: selectedProducts
          },
          tech_assistant: currentTechAssistant
        })
      });

      if (response.ok) {
        alert('AI Sales Agent configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save AI Sales Agent configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving AI Sales Agent configuration:', error);
      alert('Error saving AI Sales Agent configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTechAssistantSave = async () => {
    try {
      setSaving(true);
      // Get current sales agent config to preserve it
      const currentConfigResponse = await fetch(`${API_BASE_URL}/api/v1/config/agents`, {
        headers: getAuthHeaders()
      });
      
      let currentSalesAgent = aiSalesAgent;
      if (currentConfigResponse.ok) {
        const currentConfig = await currentConfigResponse.json();
        currentSalesAgent = currentConfig.sales_agent || aiSalesAgent;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/config/agents`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sales_agent: currentSalesAgent,
          tech_assistant: aiTechAssistant
        })
      });

      if (response.ok) {
        alert('AI Tech Assistant configuration saved successfully!');
        // Refresh configurations after successful save
        fetchConfigurations();
      } else {
        const errorData = await response.text();
        console.error('Save failed:', response.status, errorData);
        alert(`Failed to save AI Tech Assistant configuration: ${response.status} - ${errorData}`);
      }
    } catch (error) {
      console.error('Error saving AI Tech Assistant configuration:', error);
      alert('Error saving AI Tech Assistant configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSalesAgent = async () => {
    if (!testProductUrl.trim()) {
      alert('Please enter a product URL');
      return;
    }
    
    if (!testSelectedProduct) {
      alert('Please select a product');
      return;
    }

    try {
      setTestingSalesAgent(true);
      setTestResults('');
      setShowTestResults(true);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/ai-sales-assistant`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productUrl: testProductUrl,
          productCode: testSelectedProduct,
          accountId: '1'
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedResults = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              setTestResults(accumulatedResults);
              return;
            }
            accumulatedResults += data;
            setTestResults(accumulatedResults);
          }
        }
      }
    } catch (error) {
      console.error('Error testing AI Sales Agent:', error);
      setTestResults(`Error: ${error.message}`);
    } finally {
      setTestingSalesAgent(false);
    }
  };

  // Product selector utility functions

  // Account and Vector Store Functions
  const fetchAccountInfo = useCallback(async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/config/account/info`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setAccountInfo(data);
      }
    } catch (error) {
      console.error('Failed to fetch account info:', error);
    }
  }, [getAuthHeaders]);

  const fetchVectorStores = useCallback(async () => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setVectorStores(data.vector_stores || []);
        setDefaultVectorStoreIndex(data.default_vector_store_index);
      }
    } catch (error) {
      console.error('Failed to fetch vector stores:', error);
    }
  }, [getAuthHeaders]);

  const handleCreateVectorStore = async () => {
    if (!vectorStoreForm.name.trim()) return;
    
    // Check vector store limit before attempting creation
    if (vectorStores.length >= (accountInfo.max_vector_store || 1)) {
      alert(`Maximum vector store limit reached (${accountInfo.max_vector_store || 1}). Cannot create more vector stores.`);
      return;
    }
    
    setCreatingVectorStore(true);
    try {
      const authHeaders = getAuthHeaders();
      
      // Prepare the request data with assistant configuration
      const requestData = {
        name: vectorStoreForm.name,
        description: vectorStoreForm.description,
        assistant: {
          name: vectorStoreForm.assistant.name || null,
          instructions: vectorStoreForm.assistant.instructions || null,
          model: vectorStoreForm.assistant.model || null
        }
      };
      
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        await fetchVectorStores(); // Refresh the list
        handleCloseVectorStoreModal();
        alert(result.message || 'Vector store and assistant created successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to create vector store: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating vector store:', error);
      alert('Failed to create vector store. Please try again.');
    } finally {
      setCreatingVectorStore(false);
    }
  };

  const handleEditVectorStore = (store, index) => {
    setEditingVectorStore({ ...store, index });
    setVectorStoreForm({
      name: store.name,
      description: store.description,
      assistant: {
        name: store.assistant_name || '',
        instructions: store.assistant_instructions || '',
        model: store.assistant_model || ''
      }
    });
  };

  const handleUpdateVectorStore = async () => {
    if (!vectorStoreForm.name.trim() || !editingVectorStore) return;
    
    setCreatingVectorStore(true);
    try {
      const authHeaders = getAuthHeaders();
      
      // Prepare the request data with assistant configuration
      const requestData = {
        name: vectorStoreForm.name,
        description: vectorStoreForm.description,
        assistant: {
          name: vectorStoreForm.assistant.name || null,
          instructions: vectorStoreForm.assistant.instructions || null,
          model: vectorStoreForm.assistant.model || null
        }
      };
      
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores/update/${editingVectorStore.index}`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        await fetchVectorStores(); // Refresh the list
        handleCloseVectorStoreModal();
        alert('Vector store and assistant updated successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to update vector store: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating vector store:', error);
      alert('Failed to update vector store. Please try again.');
    } finally {
      setCreatingVectorStore(false);
    }
  };

  const handleDeleteVectorStore = (index) => {
    setDeleteTarget(index);
    setShowDeleteConfirm(true);
  };

  const handleSetDefaultVectorStore = async (index) => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores-default`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ vector_store_index: index })
      });

      if (response.ok) {
        setDefaultVectorStoreIndex(index);
        // You could add a success notification here
      } else {
        const errorData = await response.json();
        console.error('Failed to set default vector store:', errorData);
        alert(errorData.detail || 'Failed to set default vector store');
      }
    } catch (error) {
      console.error('Error setting default vector store:', error);
      alert('Error setting default vector store');
    }
  };

  const confirmDeleteVectorStore = async () => {
    if (deleteTarget === null) return;

    try {
      const authHeaders = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/config/vector-stores/${deleteTarget}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        await fetchVectorStores(); // Refresh the list
        alert('Vector store deleted successfully!');
      } else {
        const errorData = await response.json();
        alert(`Failed to delete vector store: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting vector store:', error);
      alert('Failed to delete vector store. Please try again.');
    } finally {
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const cancelDeleteVectorStore = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleCloseVectorStoreModal = () => {
    setShowAddVectorStore(false);
    setEditingVectorStore(null);
    setVectorStoreForm({
      name: '',
      description: '',
      assistant: {
        name: '',
        instructions: '',
        model: ''
      }
    });
  };

  // Load account info and vector stores when account tab is active
  useEffect(() => {
    if (activeTab === 'account') {
      fetchAccountInfo();
      fetchVectorStores();
    }
  }, [activeTab, fetchAccountInfo, fetchVectorStores]);

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-4">
        <div className="col">
          <h2>System Configuration</h2>
          <p className="text-muted">Configure AI providers, agents, and system settings</p>
        </div>
      </div>

      {/* Configuration Tabs */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-transparent border-0">
              <ul className="nav nav-tabs card-header-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'ai' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ai')}
                  >
                    <Bot size={20} className="me-2" />
                    AI Providers
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'agents' ? 'active' : ''}`}
                    onClick={() => setActiveTab('agents')}
                  >
                    <UserCheck size={20} className="me-2" />
                    AI Agents
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'pinecone' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pinecone')}
                  >
                    <Database size={20} className="me-2" />
                    Pinecone
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'account' ? 'active' : ''}`}
                    onClick={() => setActiveTab('account')}
                  >
                    <User size={20} className="me-2" />
                    Account
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                    onClick={() => setActiveTab('general')}
                  >
                    <Settings size={20} className="me-2" />
                    General
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'ai_openai' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ai_openai')}
                  >
                    <Bot size={20} className="me-2" />
                    AI (OpenAI)
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">


              {/* AI Configuration */}
              {activeTab === 'ai' && (
                <div>
                  {/* OpenAI Configuration */}
                  <div className="card border-0 bg-light mb-4">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <Bot size={20} className="text-primary" />
                        OpenAI Configuration
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">API Key</label>
                          <div className="input-group">
                            {!openaiKeyConfigured ? (
                              <input
                                type={showPassword ? 'text' : 'password'}
                                className="form-control"
                                value={openaiConfig.openai_api_key}
                                onChange={(e) => { openaiKeyTouched.current = true; setOpenaiConfig({...openaiConfig, openai_api_key: e.target.value}); }}
                                placeholder="Enter OpenAI API key"
                              />
                            ) : (
                              <input
                                type="password"
                                className="form-control"
                                value={openaiConfig.openai_api_key ? '••••••••••••••••' : 'configured'}
                                readOnly
                              />
                            )}
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label d-flex justify-content-between align-items-center">
                            Model (Token Limit)
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={fetchAvailableModels}
                              title="Refresh available models"
                              disabled={loadingModels}
                            >
                              {loadingModels ? (
                                <span className="spinner-border spinner-border-sm me-1" />
                              ) : (
                                <span>↻</span>
                              )}
                            </button>
                          </label>
                          <select
                            className="form-select"
                            value={openaiConfig.model}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, model: e.target.value})}
                          >
                            {availableModels.openai.length > 0 ? (
                              availableModels.openai.map((model) => (
                                <option key={model.id} value={model.id}>
                                  {formatModelDisplayName(model)}
                                </option>
                              ))
                            ) : (
                              <>
                                <option value="gpt-3.5-turbo">GPT-3.5 Turbo (16K)</option>
                                <option value="gpt-4">GPT-4 (128K)</option>
                                <option value="gpt-4-turbo">GPT-4 Turbo (128K)</option>
                                <option value="gpt-5">GPT-5 (8K)</option>
                                <option value="gpt-5-mini">GPT-5 Mini (16K)</option>
                              </>
                            )}
                          </select>
                          {availableModels.openai.length === 0 && (
                            <small className="text-muted">Loading models...</small>
                          )}
                          {availableModels.openai.length > 0 && (
                            <small className="text-muted">
                              {availableModels.openai.length} models available
                            </small>
                          )}
                          <small className="text-info d-block mt-1">
                            💡 Token limits indicate maximum context length. Higher limits allow processing longer documents.
                          </small>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Temperature</label>
                          <input
                            type="range"
                            className="form-range"
                            min="0"
                            max="2"
                            step="0.1"
                            value={openaiConfig.temperature}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, temperature: parseFloat(e.target.value)})}
                          />
                          <small className="text-muted">{openaiConfig.temperature}</small>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Max Tokens</label>
                          <input
                            type="number"
                            className="form-control"
                            value={openaiConfig.max_tokens}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, max_tokens: parseInt(e.target.value)})}
                            min="100"
                            max="4000"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary"
                          onClick={handleOpenAISave}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={20} className="me-2" />
                              Save OpenAI Config
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => testAIConnection('OpenAI')}
                        >
                          <TestTube size={20} className="me-2" />
                          Test OpenAI
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Anthropic Configuration */}
                  <div className="card border-0 bg-light">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <Bot size={20} className="text-success" />
                        Anthropic (Claude) Configuration
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">API Key</label>
                          <div className="input-group">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className="form-control"
                              value={anthropicConfig.api_key}
                              onChange={(e) => setAnthropicConfig({...anthropicConfig, api_key: e.target.value})}
                              placeholder="Enter Anthropic API key"
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label d-flex justify-content-between align-items-center">
                            Model (Token Limit)
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={fetchAvailableModels}
                              title="Refresh available models"
                              disabled={loadingModels}
                            >
                              {loadingModels ? (
                                <span className="spinner-border spinner-border-sm me-1" />
                              ) : (
                                <span>↻</span>
                              )}
                            </button>
                          </label>
                          <select
                            className="form-select"
                            value={anthropicConfig.model}
                            onChange={(e) => setAnthropicConfig({...anthropicConfig, model: e.target.value})}
                          >
                            {availableModels.claude.length > 0 ? (
                              availableModels.claude.map((model) => (
                                <option key={model.id} value={model.id}>
                                  {formatModelDisplayName(model)}
                                </option>
                              ))
                            ) : (
                              <>
                                <option value="claude-3-sonnet-20240229">Claude 3 Sonnet (200K)</option>
                                <option value="claude-3-opus-20240229">Claude 3 Opus (200K)</option>
                                <option value="claude-3-haiku-20240307">Claude 3 Haiku (200K)</option>
                                <option value="claude-3-5-sonnet">Claude 3.5 Sonnet (200K)</option>
                                <option value="claude-3-5-haiku">Claude 3.5 Haiku (200K)</option>
                              </>
                            )}
                          </select>
                          {availableModels.claude.length === 0 && (
                            <small className="text-muted">Loading models...</small>
                          )}
                          {availableModels.claude.length > 0 && (
                            <small className="text-muted">
                              {availableModels.claude.length} models available
                            </small>
                          )}
                          <small className="text-info d-block mt-1">
                            💡 Token limits indicate maximum context length. Higher limits allow processing longer documents.
                          </small>
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Temperature</label>
                          <input
                            type="range"
                            className="form-range"
                            min="0"
                            max="2"
                            step="0.1"
                            value={anthropicConfig.temperature}
                            onChange={(e) => setAnthropicConfig({...anthropicConfig, temperature: parseFloat(e.target.value)})}
                          />
                          <small className="text-muted">{anthropicConfig.temperature}</small>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Max Tokens</label>
                          <input
                            type="number"
                            className="form-control"
                            value={anthropicConfig.max_tokens}
                            onChange={(e) => setAnthropicConfig({...anthropicConfig, max_tokens: parseInt(e.target.value)})}
                            min="100"
                            max="4000"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-success"
                          onClick={handleAnthropicSave}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={20} className="me-2" />
                              Save Anthropic Config
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => testAIConnection('Anthropic')}
                        >
                          <TestTube size={20} className="me-2" />
                          Test Anthropic
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Preferred AI Engine Configuration */}
                  <div className="card border-0 bg-light mt-4">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <Settings size={20} className="text-info" />
                        Preferred AI Engine
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Default AI Engine</label>
                          <select
                            className="form-select"
                            value={preferredAIEngine.preferred_engine}
                            onChange={(e) => setPreferredAIEngine({...preferredAIEngine, preferred_engine: e.target.value})}
                          >
                            <option value="auto">Auto - Let system choose best available</option>
                            <option value="openai">OpenAI GPT</option>
                            <option value="claude">Claude</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Description</label>
                          <input
                            type="text"
                            className="form-control"
                            value={preferredAIEngine.description}
                            onChange={(e) => setPreferredAIEngine({...preferredAIEngine, description: e.target.value})}
                            placeholder="Description of the preferred engine"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-info"
                          onClick={handlePreferredAISave}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save size={20} className="me-2" />
                              Save Preferred Engine
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Agents Configuration */}
              {activeTab === 'agents' && (
                <div className="row">
                  {/* Vertical Tabs Navigation */}
                  <div className="col-md-3">
                    <div className="nav flex-column nav-pills" role="tablist">
                      <button
                        className={`nav-link text-start ${activeAgentTab === 'sales-agent' ? 'active' : ''}`}
                        onClick={() => setActiveAgentTab('sales-agent')}
                        type="button"
                      >
                        <UserCheck size={18} className="me-2" />
                        AI Sales Agent
                      </button>
                      <button
                        className={`nav-link text-start ${activeAgentTab === 'tech-assistant' ? 'active' : ''}`}
                        onClick={() => setActiveAgentTab('tech-assistant')}
                        type="button"
                      >
                        <Wrench size={18} className="me-2" />
                        AI Tech Assistant
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="col-md-9">
                    <div className="tab-content">
                      {/* AI Sales Agent Tab */}
                      {activeAgentTab === 'sales-agent' && (
                        <div className="tab-pane fade show active">
                          <div className="card border-0 bg-light">
                            <div className="card-header bg-transparent">
                              <h5 className="mb-0 d-flex align-items-center gap-2">
                                <UserCheck size={24} className="text-primary" />
                                AI Sales Agent Configuration
                              </h5>
                            </div>
                            <div className="card-body">
                              <div className="row mb-3">
                                <div className="col-md-12">
                                  <div className="form-check form-switch mb-3">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="salesAgentEnabled"
                                      checked={aiSalesAgent.enabled}
                                      onChange={(e) => setAiSalesAgent({...aiSalesAgent, enabled: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="salesAgentEnabled">
                                      <strong>Enable AI Sales Agent</strong>
                                    </label>
                                  </div>
                                  <p className="text-muted small">
                                    An agent which explains about the product from the information on site and manufacturer site. 
                                    Leads with questions a user may want to ask, then passes over to tech assistant.
                                  </p>
                                </div>
                              </div>

                              {aiSalesAgent.enabled && (
                                <>
                                  <div className="row mb-3">
                                    <div className="col-md-6">
                                      <div className="form-check form-switch d-flex align-items-start">
                                        <input
                                          className="form-check-input me-2"
                                          type="checkbox"
                                          id="salesAgentCaching"
                                          checked={aiSalesAgent.caching_enabled}
                                          onChange={(e) => setAiSalesAgent({...aiSalesAgent, caching_enabled: e.target.checked})}
                                        />
                                        <div>
                                          <label className="form-check-label" htmlFor="salesAgentCaching">
                                            <strong>Enable Caching</strong>
                                          </label>
                                          <div>
                                            <small className="text-muted">
                                              Cache responses for faster performance and reduced costs.
                                            </small>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-6">
                                      <label className="form-label">Time to Chime In (seconds)</label>
                                      <input
                                        type="number"
                                        className="form-control"
                                        value={aiSalesAgent.chime_in_time}
                                        onChange={(e) => setAiSalesAgent({...aiSalesAgent, chime_in_time: parseInt(e.target.value)})}
                                        min="5"
                                        max="300"
                                      />
                                      <small className="text-muted">
                                        Time in seconds measuring how long the user is on a page before the agent chimes in with product information.
                                      </small>
                                    </div>
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-12">
                                      <label className="form-label">Product Scope</label>
                                      <div className="mb-2">
                                        <div className="form-check">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            name="productScope"
                                            id="allProducts"
                                            value="all"
                                            checked={aiSalesAgent.product_scope === 'all'}
                                            onChange={(e) => setAiSalesAgent({...aiSalesAgent, product_scope: e.target.value})}
                                          />
                                          <label className="form-check-label" htmlFor="allProducts">
                                            Show on all product pages
                                          </label>
                                        </div>
                                        <div className="form-check">
                                          <input
                                            className="form-check-input"
                                            type="radio"
                                            name="productScope"
                                            id="selectedProducts"
                                            value="selected"
                                            checked={aiSalesAgent.product_scope === 'selected'}
                                            onChange={(e) => setAiSalesAgent({...aiSalesAgent, product_scope: e.target.value})}
                                          />
                                          <label className="form-check-label" htmlFor="selectedProducts">
                                            Show only on selected products
                                          </label>
                                        </div>
                                      </div>

                                      {aiSalesAgent.product_scope === 'selected' && (
                                        <div className="mt-3">
                                          <label className="form-label">Select Products</label>
                                          
                                          {/* Search Input */}
                                          <div className="mb-3">
                                            <input
                                              type="text"
                                              className="form-control"
                                              placeholder="Search products by code or name..."
                                              value={searchTerm}
                                              onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                          </div>

                                          <div className="row">
                                            {/* Selected Products */}
                                            <div className="col-md-6">
                                              <div className="card">
                                                <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                                                  <h6 className="mb-0">Selected Products ({selectedProductsPagination.total_count})</h6>
                                                  <button
                                                    className="btn btn-sm btn-outline-light"
                                                    onClick={clearAllProducts}
                                                    disabled={selectedProducts.length === 0}
                                                    title="Clear all selected products"
                                                  >
                                                    Clear All
                                                  </button>
                                                </div>
                                                <div className="card-body p-2" style={{ height: '250px', overflowY: 'auto' }}>
                                                  {loadingSelected ? (
                                                    <div className="text-center py-3">
                                                      <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                      </div>
                                                    </div>
                                                  ) : selectedProductsData.length === 0 ? (
                                                    <div className="text-muted text-center py-3">
                                                      <small>No products selected</small>
                                                    </div>
                                                  ) : (
                                                    <>
                                                      {selectedProductsData.map((product) => (
                                                        <div key={product.id} className="d-flex justify-content-between align-items-center p-2 border-bottom">
                                                          <div className="flex-grow-1">
                                                            <div className="fw-bold">{product.product_code}</div>
                                                            <div className="small text-muted">{product.name}</div>
                                                          </div>
                                                          <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => removeProduct(product.product_code)}
                                                            title="Remove from selection"
                                                          >
                                                            ×
                                                          </button>
                                                        </div>
                                                      ))}
                                                      
                                                      {/* Selected Products Pagination */}
                                                      {selectedProductsPagination.total_pages > 1 && (
                                                        <div className="d-flex justify-content-center mt-2">
                                                          <nav>
                                                            <ul className="pagination pagination-sm mb-0">
                                                              <li className={`page-item ${!selectedProductsPagination.has_prev ? 'disabled' : ''}`}>
                                                                <button
                                                                  className="page-link"
                                                                  onClick={() => loadSelectedProductsPage(selectedProductsPagination.current_page - 1)}
                                                                  disabled={!selectedProductsPagination.has_prev}
                                                                >
                                                                  Previous
                                                                </button>
                                                              </li>
                                                              <li className="page-item active">
                                                                <span className="page-link">
                                                                  {selectedProductsPagination.current_page} of {selectedProductsPagination.total_pages}
                                                                </span>
                                                              </li>
                                                              <li className={`page-item ${!selectedProductsPagination.has_next ? 'disabled' : ''}`}>
                                                                <button
                                                                  className="page-link"
                                                                  onClick={() => loadSelectedProductsPage(selectedProductsPagination.current_page + 1)}
                                                                  disabled={!selectedProductsPagination.has_next}
                                                                >
                                                                  Next
                                                                </button>
                                                              </li>
                                                            </ul>
                                                          </nav>
                                                        </div>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Available Products */}
                                            <div className="col-md-6">
                                              <div className="card">
                                                <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                                                  <h6 className="mb-0">Available Products ({availableProductsPagination.total_count})</h6>
                                                  <button
                                                    className="btn btn-sm btn-outline-light"
                                                    onClick={selectAllFromCurrentPage}
                                                    disabled={availableProductsData.length === 0}
                                                    title="Select all from current page"
                                                  >
                                                    Select Page
                                                  </button>
                                                </div>
                                                <div className="card-body p-2" style={{ height: '250px', overflowY: 'auto' }}>
                                                  {loadingAvailable ? (
                                                    <div className="text-center py-3">
                                                      <div className="spinner-border spinner-border-sm" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                      </div>
                                                    </div>
                                                  ) : availableProductsData.length === 0 ? (
                                                    <div className="text-muted text-center py-3">
                                                      <small>No products available</small>
                                                    </div>
                                                  ) : (
                                                    <>
                                                      {availableProductsData.map((product) => (
                                                        <div key={product.id} className="d-flex justify-content-between align-items-center p-2 border-bottom">
                                                          <div className="flex-grow-1">
                                                            <div className="fw-bold">{product.product_code}</div>
                                                            <div className="small text-muted">{product.name}</div>
                                                          </div>
                                                          <button
                                                            className="btn btn-sm btn-outline-success"
                                                            onClick={() => addProduct(product.product_code)}
                                                            title="Add to selection"
                                                          >
                                                            +
                                                          </button>
                                                        </div>
                                                      ))}
                                                      
                                                      {/* Available Products Pagination */}
                                                      {availableProductsPagination.total_pages > 1 && (
                                                        <div className="d-flex justify-content-center mt-2">
                                                          <nav>
                                                            <ul className="pagination pagination-sm mb-0">
                                                              <li className={`page-item ${!availableProductsPagination.has_prev ? 'disabled' : ''}`}>
                                                                <button
                                                                  className="page-link"
                                                                  onClick={() => loadAvailableProductsPage(availableProductsPagination.current_page - 1)}
                                                                  disabled={!availableProductsPagination.has_prev}
                                                                >
                                                                  Previous
                                                                </button>
                                                              </li>
                                                              <li className="page-item active">
                                                                <span className="page-link">
                                                                  {availableProductsPagination.current_page} of {availableProductsPagination.total_pages}
                                                                </span>
                                                              </li>
                                                              <li className={`page-item ${!availableProductsPagination.has_next ? 'disabled' : ''}`}>
                                                                <button
                                                                  className="page-link"
                                                                  onClick={() => loadAvailableProductsPage(availableProductsPagination.current_page + 1)}
                                                                  disabled={!availableProductsPagination.has_next}
                                                                >
                                                                  Next
                                                                </button>
                                                              </li>
                                                            </ul>
                                                          </nav>
                                                        </div>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Selection Summary */}
                                          <div className="mt-3 text-center">
                                            <small className="text-muted">
                                              {selectedProducts.length} products selected
                                            </small>
                                          </div>

                                          <div className="mt-2">
                                            <small className="text-muted">
                                              Use the search box to filter products and click + to add or × to remove products.
                                            </small>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-12">
                                      <label className="form-label">System Prompt</label>
                                      <textarea
                                        className="form-control"
                                        rows="4"
                                        value={aiSalesAgent.system_prompt}
                                        onChange={(e) => setAiSalesAgent({...aiSalesAgent, system_prompt: e.target.value})}
                                        placeholder="Enter the system prompt that defines how the AI Sales Agent should behave..."
                                      />
                                      <small className="text-muted">
                                        This prompt defines the AI Sales Agent's personality, behavior, and instructions for interacting with customers.
                                      </small>
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* AI Sales Agent Testing Interface */}
                              {aiSalesAgent.enabled && (
                                <div className="mt-4">
                                  <div className="card border-primary">
                                    <div className="card-header bg-primary text-white">
                                      <h6 className="mb-0 d-flex align-items-center gap-2">
                                        <TestTube size={20} />
                                        Test AI Sales Agent
                                      </h6>
                                    </div>
                                    <div className="card-body">
                                      <div className="row mb-3">
                                        <div className="col-md-12">
                                          <label className="form-label fw-semibold">Product URL</label>
                                          <input
                                            type="url"
                                            className="form-control"
                                            value={testProductUrl}
                                            onChange={(e) => setTestProductUrl(e.target.value)}
                                            placeholder="Enter product URL (e.g., https://www.herculesindustries.com/...)"
                                          />
                                          <small className="text-muted">
                                            Enter the URL of the product page you want the AI Sales Agent to analyze.
                                          </small>
                                        </div>
                                      </div>
                                      
                                      <div className="row mb-3">
                                        <div className="col-md-12">
                                          <label className="form-label fw-semibold">Select Product</label>
                                          <select
                                            className="form-select"
                                            value={testSelectedProduct}
                                            onChange={(e) => setTestSelectedProduct(e.target.value)}
                                          >
                                            <option value="">Choose a product...</option>
                                            {aiSalesAgent.product_scope === 'all' ? (
                                              // If scope is 'all', show all available products
                                              availableProductsData?.map((product) => (
                                                <option key={product.product_code} value={product.product_code}>
                                                  {product.product_code} - {product.name}
                                                </option>
                                              ))
                                            ) : (
                                              // If scope is 'selected', show only selected products
                                              aiSalesAgent.selected_products?.map((productCode) => {
                                                const product = availableProductsData?.find(p => p.product_code === productCode);
                                                return (
                                                  <option key={productCode} value={productCode}>
                                                    {productCode} {product ? `- ${product.name}` : ''}
                                                  </option>
                                                );
                                              })
                                            )}
                                          </select>
                                          <small className="text-muted">
                                            {aiSalesAgent.product_scope === 'all' ? 
                                              'All products are available for testing.' :
                                              `Only products in the selected scope (${aiSalesAgent.selected_products?.length || 0} products) are available.`
                                            }
                                          </small>
                                        </div>
                                      </div>

                                      <div className="d-flex gap-2 mb-3">
                                        <button
                                          className="btn btn-success"
                                          onClick={handleTestSalesAgent}
                                          disabled={testingSalesAgent || !testProductUrl.trim() || !testSelectedProduct}
                                        >
                                          {testingSalesAgent ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm me-2" />
                                              Testing...
                                            </>
                                          ) : (
                                            <>
                                              <TestTube size={18} className="me-2" />
                                              Test AI Sales Agent
                                            </>
                                          )}
                                        </button>
                                        {showTestResults && (
                                          <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => setShowTestResults(false)}
                                          >
                                            Hide Results
                                          </button>
                                        )}
                                      </div>

                                      {/* Test Results */}
                                      {showTestResults && (
                                        <div className="mt-3">
                                          <label className="form-label fw-semibold">AI Sales Agent Response</label>
                                          <div 
                                            className="form-control" 
                                            style={{ 
                                              minHeight: '200px', 
                                              maxHeight: '400px', 
                                              overflowY: 'auto',
                                              fontFamily: 'inherit',
                                              padding: '15px'
                                            }}
                                          >
                                            {testResults ? (
                                            testResults.startsWith('Error:') ? (
                                              <div className="text-danger">{testResults}</div>
                                            ) : (
                                              <div className="bg-light p-3 rounded border response-container">
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                  <Bot size={16} className="text-success" />
                                                  <span className="fw-semibold text-success">AI Sales Assistant Response</span>
                                                </div>
                                                <div className="clean-response">
                                                  <ReactMarkdown
                                                    components={{
                                                      code({ node, inline, className, children, ...props }) {
                                                        const match = /language-(\w+)/.exec(className || "");
                                                        return !inline && match ? (
                                                          <SyntaxHighlighter
                                                            style={materialLight}
                                                            language={match[1]}
                                                            PreTag="div"
                                                            {...props}
                                                          >
                                                            {String(children).replace(/\n$/, "")}
                                                          </SyntaxHighlighter>
                                                        ) : (
                                                          <code className={className} {...props}>
                                                            {children}
                                                          </code>
                                                        );
                                                      }
                                                    }}
                                                  >
                                                    {testResults}
                                                  </ReactMarkdown>
                                                </div>
                                              </div>
                                            )
                                          ) : (
                                            testingSalesAgent ? (
                                              <div className="text-muted">
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Generating AI response...
                                              </div>
                                            ) : (
                                              <div className="text-muted">Test results will appear here...</div>
                                            )
                                          )}
                                          </div>
                                          <small className="text-muted">
                                            This shows the real-time response from the AI Sales Agent endpoint.
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Sales Agent Save Button */}
                              <div className="d-flex gap-2 mt-4">
                                <button
                                  className="btn btn-primary"
                                  onClick={handleSalesAgentSave}
                                  disabled={saving}
                                >
                                  {saving ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" />
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <Save size={20} className="me-2" />
                                      Save Sales Agent Configuration
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* AI Tech Assistant Tab */}
                      {activeAgentTab === 'tech-assistant' && (
                        <div className="tab-pane fade show active">
                          <div className="card border-0 bg-light">
                            <div className="card-header bg-transparent">
                              <h5 className="mb-0 d-flex align-items-center gap-2">
                                <Wrench size={24} className="text-success" />
                                AI Tech Assistant Configuration
                              </h5>
                            </div>
                            <div className="card-body">
                              <div className="row mb-3">
                                <div className="col-md-12">
                                  <div className="form-check form-switch mb-3">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id="techAssistantEnabled"
                                      checked={aiTechAssistant.enabled}
                                      onChange={(e) => setAiTechAssistant({...aiTechAssistant, enabled: e.target.checked})}
                                    />
                                    <label className="form-check-label" htmlFor="techAssistantEnabled">
                                      <strong>Enable AI Tech Assistant</strong>
                                    </label>
                                  </div>
                                  <p className="text-muted small">
                                    Provides technical support and detailed guidance based on product manuals and specifications.
                                  </p>
                                </div>
                              </div>

                              {aiTechAssistant.enabled && (
                                <>
                                  <div className="row mb-3">
                                    <div className="col-md-12">
                                      <label className="form-label">Description</label>
                                      <textarea
                                        className="form-control"
                                        rows="3"
                                        value={aiTechAssistant.description}
                                        onChange={(e) => setAiTechAssistant({...aiTechAssistant, description: e.target.value})}
                                        placeholder="Describe what the AI Tech Assistant does..."
                                      />
                                      <small className="text-muted">
                                        This description helps users understand what the Tech Assistant can help them with.
                                      </small>
                                    </div>
                                  </div>

                                  <div className="row mb-3">
                                    <div className="col-md-12">
                                      <label className="form-label">System Prompt</label>
                                      <textarea
                                        className="form-control"
                                        rows="4"
                                        value={aiTechAssistant.system_prompt}
                                        onChange={(e) => setAiTechAssistant({...aiTechAssistant, system_prompt: e.target.value})}
                                        placeholder="Enter the system prompt that defines how the AI Tech Assistant should behave..."
                                      />
                                      <small className="text-muted">
                                        This prompt defines the AI Tech Assistant's expertise, tone, and instructions for providing technical support.
                                      </small>
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* Tech Assistant Save Button */}
                              <div className="d-flex gap-2 mt-4">
                                <button
                                  className="btn btn-primary"
                                  onClick={handleTechAssistantSave}
                                  disabled={saving}
                                >
                                  {saving ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-2" />
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <Save size={20} className="me-2" />
                                      Save Tech Assistant Configuration
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* General Configuration */}
              {activeTab === 'general' && (
                <div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">App Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={generalConfig.app_name}
                        onChange={(e) => setGeneralConfig({...generalConfig, app_name: e.target.value})}
                        placeholder="AI Tech Assistant"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Max File Size (MB)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={generalConfig.max_file_size}
                        onChange={(e) => setGeneralConfig({...generalConfig, max_file_size: parseInt(e.target.value)})}
                        min="1"
                        max="100"
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Chunk Size</label>
                      <input
                        type="number"
                        className="form-control"
                        value={generalConfig.chunk_size}
                        onChange={(e) => setGeneralConfig({...generalConfig, chunk_size: parseInt(e.target.value)})}
                        min="100"
                        max="5000"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Chunk Overlap</label>
                      <input
                        type="number"
                        className="form-control"
                        value={generalConfig.chunk_overlap}
                        onChange={(e) => setGeneralConfig({...generalConfig, chunk_overlap: parseInt(e.target.value)})}
                        min="0"
                        max="1000"
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Rate Limit (per minute)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={generalConfig.rate_limit_per_minute}
                        onChange={(e) => setGeneralConfig({...generalConfig, rate_limit_per_minute: parseInt(e.target.value)})}
                        min="10"
                        max="1000"
                      />
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={handleGeneralSave}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={20} className="me-2" />
                          Save Configuration
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Pinecone Configuration */}
              {activeTab === 'pinecone' && (
                <div>
                  <div className="card border-0 bg-light mb-4">
                    <div className="card-header bg-transparent">
                      <h6 className="mb-0 d-flex align-items-center gap-2">
                        <Database size={20} className="text-primary" />
                        Pinecone Configuration
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">API Key</label>
                          <input
                            type="password"
                            className="form-control"
                            value={pineconeConfig.api_key}
                            onChange={(e) => setPineconeConfig({ ...pineconeConfig, api_key: e.target.value })}
                            placeholder="Enter Pinecone API key"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Index</label>
                          <input
                            className="form-control"
                            value={pineconeConfig.index}
                            onChange={(e) => setPineconeConfig({ ...pineconeConfig, index: e.target.value })}
                            placeholder="index-name"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Environment/Host</label>
                          <input
                            className="form-control"
                            value={pineconeConfig.environment}
                            onChange={(e) => setPineconeConfig({ ...pineconeConfig, environment: e.target.value })}
                            placeholder="e.g. us-east-1-aws or api-xxxxx.svc.us-east-1-aws.pinecone.io"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-3">
                        <button
                          className="btn btn-primary"
                          onClick={async () => {
                            try {
                              setSaving(true);
                              const res = await fetch(`${API_BASE_URL}/api/v1/config/pinecone`, {
                                method: 'PUT',
                                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                                body: JSON.stringify(pineconeConfig)
                              });
                              if (!res.ok) {
                                const t = await res.text();
                                alert(`Save failed: ${res.status} ${t}`);
                              } else {
                                alert('Pinecone configuration saved');
                              }
                            } finally {
                              setSaving(false);
                            }
                          }}
                        >
                          <Save size={20} className="me-2" />
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Configuration */}
              {activeTab === 'account' && (
                <div>
                  <h5 className="mb-4">Account & Vector Store Management</h5>
                  
                  {/* Account Information */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card border-0 bg-light">
                        <div className="card-body">
                          <h6 className="card-title d-flex align-items-center">
                            <User size={20} className="me-2 text-primary" />
                            Account Information
                          </h6>
                          <div className="row">
                            <div className="col-md-6">
                              <label className="form-label">Account ID</label>
                              <input
                                type="text"
                                className="form-control"
                                value={accountInfo.account_id || 'Loading...'}
                                disabled
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Vector Store Pattern</label>
                              <input
                                type="text"
                                className="form-control"
                                value={`extremeb2b-${accountInfo.account_id || 'X'}`}
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Vector Stores Management */}
                  <div className="row">
                    <div className="col-12">
                      <div className="card">
                        <div className="card-header bg-transparent">
                          <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 d-flex align-items-center">
                              <Database size={20} className="me-2 text-success" />
                              Vector Stores
                            </h6>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => setShowAddVectorStore(true)}
                              disabled={vectorStores.length >= (accountInfo.max_vector_store || 1)}
                              title={vectorStores.length >= (accountInfo.max_vector_store || 1) 
                                ? `Maximum vector store limit reached (${accountInfo.max_vector_store || 1})` 
                                : 'Add a new vector store'}
                            >
                              <Plus size={16} className="me-1" />
                              Add Vector Store
                              {vectorStores.length >= (accountInfo.max_vector_store || 1) && (
                                <span className="badge bg-warning ms-1">Limit Reached</span>
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          {vectorStores.length === 0 ? (
                            <div className="text-center text-muted py-4">
                              <Database size={48} className="mb-2 opacity-50" />
                              <p>No vector stores configured yet.</p>
                              <p className="small">Create a vector store to organize your document embeddings.</p>
                            </div>
                          ) : (
                            <div className="row">
                                                                      {vectorStores.map((store, index) => (
                                            <div key={index} className="col-md-6 mb-3">
                                                <div className={`card border ${defaultVectorStoreIndex === index ? 'border-success' : ''}`}>
                                                    <div className="card-body">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div className="d-flex align-items-center">
                                                                <h6 className="card-title mb-0 me-2">{store.name}</h6>
                                                                {defaultVectorStoreIndex === index && (
                                                                    <span className="badge bg-success">Default</span>
                                                                )}
                                                                {store.assistant_id && (
                                                                    <span className="badge bg-info ms-1" title={store.assistant_name}>
                                                                        🤖 Assistant
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="dropdown">
                                                                <button
                                                                    className="btn btn-sm btn-outline-secondary dropdown-toggle"
                                                                    type="button"
                                                                    data-bs-toggle="dropdown"
                                                                >
                                                                    Actions
                                                                </button>
                                                                <ul className="dropdown-menu">
                                                                    {defaultVectorStoreIndex !== index && (
                                                                        <li>
                                                                            <button
                                                                                className="dropdown-item"
                                                                                onClick={() => handleSetDefaultVectorStore(index)}
                                                                            >
                                                                                <Database size={14} className="me-2" />
                                                                                Set as Default
                                                                            </button>
                                                                        </li>
                                                                    )}
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handleEditVectorStore(store, index)}
                                                                        >
                                                                            <Edit2 size={14} className="me-2" />
                                                                            Edit
                                                                        </button>
                                                                    </li>
                                                                    <li>
                                                                        <button
                                                                            className="dropdown-item text-danger"
                                                                            onClick={() => handleDeleteVectorStore(index)}
                                                                        >
                                                                            <Trash2 size={14} className="me-2" />
                                                                            Delete
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                        <p className="card-text text-muted small mb-2">{store.description}</p>
                                                        <div className="d-flex justify-content-between text-small">
                                                            <span className="badge bg-primary">{store.vector_store_id}</span>
                                                            <span className="text-muted">
                                                                Created: {new Date(store.created_at).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI (OpenAI) Configuration */}
              {activeTab === 'ai_openai' && (
                <div>
                  {/* OpenAI Configuration */}
                  <div className="card border-0 bg-light mb-4">
                    <div className="card-body">
                      <h5 className="card-title">OpenAI Configuration</h5>
                      {/* reuse existing OpenAI inputs block */}
                      {/* BEGIN OPENAI BLOCK */}
                      <div className="row g-3">
                        <div className="col-md-12">
                          <label className="form-label">API Key</label>
                          {!openaiKeyConfigured ? (
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className="form-control"
                              value={openaiConfig.openai_api_key}
                              onChange={(e) => { openaiKeyTouched.current = true; setOpenaiConfig({...openaiConfig, openai_api_key: e.target.value}); }}
                              placeholder="Enter OpenAI API key"
                            />
                          ) : (
                            <input
                              type="password"
                              className="form-control"
                              value={openaiConfig.openai_api_key ? '••••••••••••••••' : 'configured'}
                              readOnly
                            />
                          )}
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Model</label>
                          <select
                            className="form-select"
                            value={openaiConfig.model}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, model: e.target.value})}
                          >
                            {availableModels.openai.length > 0 ? (
                              availableModels.openai.map((model, idx) => (
                                <option key={idx} value={model}>{model}</option>
                              ))
                            ) : (
                              <>
                                <option value="gpt-4o">gpt-4o</option>
                                <option value="gpt-4o-mini">gpt-4o-mini</option>
                                <option value="gpt-4-turbo">gpt-4-turbo</option>
                              </>
                            )}
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Temperature</label>
                          <input
                            type="range"
                            className="form-range"
                            min="0"
                            max="2"
                            step="0.1"
                            value={openaiConfig.temperature}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, temperature: parseFloat(e.target.value)})}
                          />
                          <small className="text-muted">{openaiConfig.temperature}</small>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label">Max Tokens</label>
                          <input
                            type="number"
                            className="form-control"
                            value={openaiConfig.max_tokens}
                            onChange={(e) => setOpenaiConfig({...openaiConfig, max_tokens: parseInt(e.target.value)})}
                            min="100"
                            max="4000"
                          />
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-3">
                        <button className="btn btn-primary" onClick={handleOpenAISave} disabled={saving}>
                          {saving ? (<><span className="spinner-border spinner-border-sm me-2" />Saving...</>) : 'Save OpenAI'}
                        </button>
                        <button className="btn btn-outline-secondary" onClick={testAIConnection}>
                          Test Connection
                        </button>
                      </div>
                      {/* END OPENAI BLOCK */}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Add/Edit Vector Store Modal - Right Sliding */}
      {(showAddVectorStore || editingVectorStore) && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div 
            className="modal-dialog modal-dialog-slideright" 
            style={{ 
              position: 'fixed',
              right: 0,
              top: 0,
              height: '100vh',
              width: '50%',
              maxWidth: '75%',
              minWidth: '40%',
              margin: 0,
              resize: 'horizontal',
              overflow: 'auto'
            }}
          >
            <div className="modal-content" style={{ height: '100%', borderRadius: '0' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid #dee2e6', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1000 }}>
                <h5 className="modal-title">
                  🤖 {editingVectorStore ? 'Edit Vector Store & Assistant' : 'Add New Vector Store & Assistant'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseVectorStoreModal}
                ></button>
              </div>
              <div className="modal-body" style={{ padding: '1.5rem', overflow: 'auto', flex: 1 }}>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={vectorStoreForm.name}
                    onChange={(e) => setVectorStoreForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Product Documentation Store"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={vectorStoreForm.description}
                    onChange={(e) => setVectorStoreForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this vector store will contain..."
                  />
                </div>
                
                {/* Assistant Configuration Section */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">🤖 Assistant Configuration</h6>
                    <small className="text-muted">Configure the AI assistant that will be attached to this vector store</small>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">Assistant Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={vectorStoreForm.assistant.name}
                        onChange={(e) => setVectorStoreForm(prev => ({ 
                          ...prev, 
                          assistant: { ...prev.assistant, name: e.target.value }
                        }))}
                        placeholder={`Support Assistant - ${vectorStoreForm.name || 'Vector Store'}`}
                      />
                      <small className="form-text text-muted">
                        Leave empty to auto-generate: "Support Assistant - {vectorStoreForm.name}"
                      </small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Assistant Instructions</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={vectorStoreForm.assistant.instructions}
                        onChange={(e) => setVectorStoreForm(prev => ({ 
                          ...prev, 
                          assistant: { ...prev.assistant, instructions: e.target.value }
                        }))}
                        placeholder="You are a helpful support AI. Use the attached vector store to answer questions based only on product manuals."
                      />
                      <small className="form-text text-muted">
                        Define how the assistant should behave and respond to user queries
                      </small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">GPT Model</label>
                      <input
                        type="text"
                        className="form-control"
                        value={vectorStoreForm.assistant.model}
                        onChange={(e) => setVectorStoreForm(prev => ({ 
                          ...prev, 
                          assistant: { ...prev.assistant, model: e.target.value }
                        }))}
                        placeholder="gpt-4o"
                      />
                      <small className="form-text text-muted">
                        Enter the GPT model for your assistant (e.g., gpt-4o, gpt-4o-mini, gpt-4-turbo, gpt-4, gpt-3.5-turbo). Leave empty to use the default (gpt-4o)
                      </small>
                    </div>
                  </div>
                </div>
                
                {!editingVectorStore && (
                  <div className="alert alert-info">
                    <small>
                      <strong>Note:</strong> This will create a new OpenAI vector store with ID format: 
                      <code>extremeb2b-{accountInfo.account_id}</code> and an OpenAI assistant attached to it.
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #dee2e6', position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1000 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseVectorStoreModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={editingVectorStore ? handleUpdateVectorStore : handleCreateVectorStore}
                  disabled={creatingVectorStore || !vectorStoreForm.name.trim()}
                >
                  {creatingVectorStore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {editingVectorStore ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      {editingVectorStore ? 'Update Store' : 'Create Store'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDeleteVectorStore}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this vector store?</p>
                <p className="text-muted">
                  <strong>This action cannot be undone.</strong> All data associated with this vector store will be permanently removed.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDeleteVectorStore}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDeleteVectorStore}
                >
                  Delete Vector Store
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Configurations;
